import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Modal,
  Snackbar,
  Typography,
} from "@mui/material";
import { AppDispatch } from "@store/store";
import {
  resetTermsAndConditionState,
  updateTermsAndConditions,
} from "@store/userSlice";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DOMPurify from "dompurify";
import useAuth0 from "@common/Auth0Provider/use-auth0";

interface TermsAndConditionsProps {
  setShowTermsAndConditions: React.Dispatch<React.SetStateAction<boolean>>;
  showTermsAndConditions: boolean;
}

const TermsAndConditions: React.FC<TermsAndConditionsProps> = ({
  setShowTermsAndConditions,
  showTermsAndConditions,
}) => {
  const [checked, setChecked] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const { logout } = useAuth0();
  const dispatch = useDispatch<AppDispatch>();
  const {
    updateTermsAndConditionsloading,
    termsAndConditionsError,
    termsAndConditions,
  } = useSelector((state: any) => state.user);

  useEffect(() => {
    if (!updateTermsAndConditionsloading && !termsAndConditionsError) {
      setShowLoader(false);
      setShowTermsAndConditions(false);
    }
  }, [updateTermsAndConditionsloading, termsAndConditionsError]);

  const handleAlertClose = () => {
    setShowLoader(false);
    dispatch(resetTermsAndConditionState());
  };

  const handleTermsAndConditions = () => {
    const payload = {
      isTermsAndConditionsAccepted: checked,
      termsAndConditionsAcceptedVersion: termsAndConditions.version,
    };
    dispatch(updateTermsAndConditions(payload));
    setShowLoader(true);
  };

  return (
    <>
      <Modal
        open={showTermsAndConditions}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        {termsAndConditions?.content ? (
          <Box
            sx={{
              position: "relative",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "85%", sm: "94%" },
              bgcolor: "background.paper",
              boxShadow: 24,
              borderRadius: "4px",
              pt: 2,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Box className="px-2 md:px-4">
              <Box
                component="div"
                sx={{
                  maxHeight: "calc(100vh - 160px)",
                  overflowY: "auto",
                  color: "#000000",
                  fontWeight: 400,
                  whiteSpace: "pre-line",
                  fontSize: { xs: "0.8rem", sm: "1rem" },
                  "& ul": {
                    paddingLeft: "1.5rem",
                    listStyleType: "disc",
                  },
                  "& li": {
                  },
                }}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(termsAndConditions?.content, {
                    ADD_ATTR: ["onclick"],
                  }),
                }}
              />
            </Box>
            <Card className="flex flex-col md:flex-row border-t-2 border-gray-200 gap-2 md:gap-0 p-2 md:p-4 justify-between inset-shadow-xs">
              <Box className="flex items-center">
                <Checkbox
                  checked={checked}
                  onChange={(e) => setChecked(e.target.checked)}
                  sx={{
                    display: "flex",
                    padding: "0 0.2rem 0 0",
                    alignItems: "flex-start",
                  }}
                />
                <Typography sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }}>
                  I acknowledge all the Terms & Conditions.
                </Typography>
              </Box>
              <Box className="flex gap-2">
                <Button
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #A1A3A6",
                    color: "rgba(161, 163, 166, 1)",
                    padding: "0 1.2rem",
                    fontWeight:400
                  }}
                  onClick={() =>
                    logout({
                      logoutParams: { returnTo: window.location.origin },
                    })
                  }
                >
                  Back
                </Button>
                <Button
                  sx={{
                    textTransform: "none",
                    backgroundColor: !checked ? "#D4D6D9" : "#318FE0",
                    color: "#FFFFFF",
                    padding: "0 1rem",
                    fontWeight:400

                  }}
                  onClick={handleTermsAndConditions}
                >
                  <Typography className="!flex !gap-2 !items-center">
                    {showLoader && updateTermsAndConditionsloading && (
                      <CircularProgress
                        style={{ color: "white" }}
                        size="15px"
                      />
                    )}{" "}
                    Accept
                  </Typography>
                </Button>
              </Box>
            </Card>
          </Box>
        ) : (
          <Box
            sx={{
              position: "relative",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "80%", sm: "90%" },
              bgcolor: "background.paper",
              boxShadow: 24,
              borderRadius: "4px",
              pt: 2,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              minHeight: "calc(100vh - 160px)",
              alignItems: "center",
            }}
          >
            <CircularProgress size="20px" />
          </Box>
        )}
      </Modal>
      <Snackbar
        className="!pt-[2rem] md:!pt-6"
        open={!updateTermsAndConditionsloading && termsAndConditionsError}
        autoHideDuration={3000}
        onClose={handleAlertClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert variant="filled" severity="error">
          {termsAndConditionsError}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TermsAndConditions;
